import type { WorkoutPlanResult } from '../coachTypes';

const inp =
  'w-full rounded-lg border border-white/10 bg-zinc-900 px-2 py-1.5 text-xs text-white placeholder:text-zinc-600';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export function WorkoutPlanEditor({
  plan,
  onChange,
}: {
  plan: WorkoutPlanResult;
  onChange: (p: WorkoutPlanResult) => void;
}) {
  function setSummary(summary: string) {
    onChange({ ...plan, summary });
  }

  function setSplitLabel(split: string) {
    onChange({ ...plan, split });
  }

  function setDayField(i: number, field: 'day' | 'focus', value: string) {
    const days = plan.days.map((d, idx) => (idx === i ? { ...d, [field]: value } : d));
    onChange({ ...plan, days });
  }

  function setExerciseField(
    di: number,
    ei: number,
    field: 'name' | 'sets' | 'reps' | 'notes',
    value: string | number,
  ) {
    const days = plan.days.map((d, di0) => {
      if (di0 !== di) return d;
      const exercises = d.exercises.map((ex, ei0) =>
        ei0 === ei ? { ...ex, [field]: field === 'sets' ? Number(value) || 0 : value } : ex,
      );
      return { ...d, exercises };
    });
    onChange({ ...plan, days });
  }

  function addExercise(di: number) {
    const days = plan.days.map((d, di0) =>
      di0 === di
        ? {
            ...d,
            exercises: [...d.exercises, { name: 'New lift', sets: 3, reps: '8–10', notes: '' }],
          }
        : d,
    );
    onChange({ ...plan, days });
  }

  function removeExercise(di: number, ei: number) {
    const days = plan.days.map((d, di0) =>
      di0 === di ? { ...d, exercises: d.exercises.filter((_, j) => j !== ei) } : d,
    );
    onChange({ ...plan, days });
  }

  function addDay() {
    const nextIdx = plan.days.length;
    const dayName = WEEKDAYS[nextIdx % WEEKDAYS.length] ?? `Day ${nextIdx + 1}`;
    onChange({
      ...plan,
      days: [
        ...plan.days,
        {
          day: dayName,
          focus: '',
          exercises: [],
        },
      ],
    });
  }

  function removeDay(di: number) {
    if (plan.days.length <= 1) return;
    onChange({ ...plan, days: plan.days.filter((_, i) => i !== di) });
  }

  function setProgressionText(text: string) {
    const lines = text
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    onChange({ ...plan, progressionNotes: lines });
  }

  return (
    <div className="space-y-4 text-sm">
      <div>
        <label className="text-xs text-zinc-500">Summary</label>
        <textarea
          value={plan.summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={3}
          className={`${inp} mt-1 resize-y`}
        />
      </div>
      <div>
        <label className="text-xs text-zinc-500">Split label</label>
        <input
          value={plan.split}
          onChange={(e) => setSplitLabel(e.target.value)}
          className={`${inp} mt-1`}
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
          Training days ({plan.days.length})
        </p>
        <button
          type="button"
          onClick={addDay}
          className="rounded-lg border border-sky-400/30 bg-sky-500/10 px-2.5 py-1 text-xs font-semibold text-sky-300 hover:bg-sky-500/20"
        >
          + Add day
        </button>
      </div>

      <div className="space-y-3">
        {plan.days.map((d, di) => (
          <div key={`day-${di}`} className="rounded-2xl border border-white/8 bg-white/[0.03] p-3">
            <div className="mb-2 flex items-start justify-between gap-2">
              <p className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">Day {di + 1}</p>
              {plan.days.length > 1 ? (
                <button
                  type="button"
                  onClick={() => removeDay(di)}
                  className="text-[10px] font-semibold text-rose-400/80 hover:text-rose-300"
                >
                  Remove day
                </button>
              ) : null}
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div>
                <label className="text-[10px] uppercase tracking-wide text-zinc-600">Day label</label>
                <input
                  value={d.day}
                  onChange={(e) => setDayField(di, 'day', e.target.value)}
                  placeholder="Monday, Day 1, Rest day…"
                  className={`${inp} mt-0.5`}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-zinc-600">Focus / instructions</label>
                <input
                  value={d.focus}
                  onChange={(e) => setDayField(di, 'focus', e.target.value)}
                  placeholder="Push, rest, mobility, cardio…"
                  className={`${inp} mt-0.5`}
                />
              </div>
            </div>
            <p className="mt-2 text-[10px] text-zinc-600">
              Exercises are optional — use focus text alone for rest, cardio, or mobility days.
            </p>
            {d.exercises.length > 0 ? (
              <ul className="mt-3 space-y-2">
                {d.exercises.map((ex, ei) => (
                  <li
                    key={`${di}-${ei}`}
                    className="grid gap-2 rounded-xl border border-white/5 bg-black/20 p-2 sm:grid-cols-12"
                  >
                    <input
                      value={ex.name}
                      onChange={(e) => setExerciseField(di, ei, 'name', e.target.value)}
                      placeholder="Exercise"
                      className={`${inp} sm:col-span-5`}
                    />
                    <input
                      type="number"
                      min={1}
                      value={ex.sets}
                      onChange={(e) => setExerciseField(di, ei, 'sets', e.target.value)}
                      className={`${inp} sm:col-span-2`}
                    />
                    <input
                      value={ex.reps}
                      onChange={(e) => setExerciseField(di, ei, 'reps', e.target.value)}
                      placeholder="Reps"
                      className={`${inp} sm:col-span-3`}
                    />
                    <div className="flex gap-1 sm:col-span-2">
                      <input
                        value={ex.notes ?? ''}
                        onChange={(e) => setExerciseField(di, ei, 'notes', e.target.value)}
                        placeholder="Notes"
                        className={`${inp} flex-1`}
                      />
                      <button
                        type="button"
                        onClick={() => removeExercise(di, ei)}
                        className="shrink-0 rounded-lg px-2 text-xs text-zinc-500 hover:bg-rose-500/20 hover:text-rose-300"
                      >
                        ×
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 rounded-xl border border-dashed border-white/8 px-3 py-2 text-xs text-zinc-500">
                No exercises on this day — the focus text above is enough.
              </p>
            )}
            <button
              type="button"
              onClick={() => addExercise(di)}
              className="mt-2 text-xs text-sky-400 hover:text-sky-300"
            >
              + Add exercise
            </button>
          </div>
        ))}
      </div>

      <div>
        <label className="text-xs text-zinc-500">Progression notes (one per line)</label>
        <textarea
          value={plan.progressionNotes.join('\n')}
          onChange={(e) => setProgressionText(e.target.value)}
          rows={4}
          className={`${inp} mt-1 resize-y font-mono text-xs`}
        />
      </div>
    </div>
  );
}
